import React, { useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye } from 'lucide-react';
import RelatedRecords from '../nativeitems/RelatedRecords';
import AllFieldsPanel from './AllFieldsPanel';
import AttachmentsTab from './AttachmentsTab';
import { useRecordDetail, fmtMoney, fmtDate, yesNo, statusLabel, SO_STATUS } from './recordUtils';
import '../nativeitems/NativeItems.css';

// Customer record (Native, read-only) — tasks #62/#60. The first customer
// record page anywhere in the platform: identity, A/R standing, terms,
// rep/private-label attribution from acct_customers, recent orders +
// invoices, and the full ns_raw capture on the All-fields tab.

export default function CustomerRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const { data, loading, error } = useRecordDetail(`/api/native-records/customers/${encodeURIComponent(id)}`);

  if (loading) return <div className="nit-page"><div className="nit-loading">Loading customer…</div></div>;
  if (error) {
    return (
      <div className="nit-page">
        <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>
        <div className="nit-error">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { record, recentOrders, recentInvoices, nativeCustomerId } = data;
  const displayName = record.alt_name || record.company_name || record.full_name || 'Customer';
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'orders', label: 'Orders', count: recentOrders.length },
    { key: 'invoices', label: 'Invoices', count: recentInvoices.length },
    { key: 'fields', label: 'All fields' },
    { key: 'related', label: 'Related' },
    { key: 'attachments', label: 'Attachments' },
  ];

  return (
    <div className="nit-page">
      <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>

      <div className="nit-item-head" style={{ marginTop: 8 }}>
        {record.entity_id && <code>{record.entity_id}</code>}
        <h1>{displayName}</h1>
        {record.is_active === false && <span className="nit-tag nit-tag-inactive">inactive</span>}
        {record.on_credit_hold && <span className="nit-tag nit-tag-warn">credit hold</span>}
        {record.ns_internal_id && <span className="nit-tag">NS {record.ns_internal_id}</span>}
        <span className="nit-readonly"><Eye size={12} /> read-only</span>
        <Link to={`/records/customers/new?copyFrom=${encodeURIComponent(id)}`} className="nit-btn" style={{ marginLeft: 'auto', textDecoration: 'none' }}>Make Copy</Link>
        <button className="nit-btn" onClick={async () => {
          if (!window.confirm('Void (inactivate) this record? History is kept; it stops appearing in pickers.')) return;
          try {
            const { data } = await axios.post(`/api/native-records/customers/${encodeURIComponent(id)}/void`);
            window.alert(`Inactivated (NetSuite: ${data.nsSync}).`);
            window.location.reload();
          } catch (err) { window.alert(err.response?.data?.error || err.message); }
        }}>Void</button>
      </div>

      <div className="nit-tabs">
        {tabs.map((t) => (
          <button key={t.key} className={`nit-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
            {t.count != null && <span className="nit-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="nit-grid">
          <div className="nit-card">
            <h3>Identity</h3>
            <div className="nit-facts">
              <span>Company</span><strong>{record.company_name || '—'}</strong>
              <span>Email</span><strong>{record.email || '—'}</strong>
              <span>Phone</span><strong>{record.phone || '—'}</strong>
              <span>Category</span><strong>{record.category || '—'}</strong>
              <span>Territory</span><strong>{record.territory || '—'}</strong>
              <span>Created in NS</span><strong>{fmtDate(record.date_created)}</strong>
            </div>
          </div>

          <div className="nit-card">
            <h3>A/R standing</h3>
            <div className="nit-facts">
              <span>Balance</span><strong>{fmtMoney(record.balance)}</strong>
              <span>Overdue</span><strong>{fmtMoney(record.overdue_balance)}</strong>
              <span>Days overdue</span><strong>{record.days_overdue ?? '—'}</strong>
              <span>Deposit balance</span><strong>{fmtMoney(record.deposit_balance)}</strong>
              <span>Unbilled orders</span><strong>{fmtMoney(record.unbilled_orders)}</strong>
              <span>Terms</span><strong>{record.payment_terms || '—'}</strong>
              <span>Credit hold</span><strong>{yesNo(record.on_credit_hold)}</strong>
            </div>
          </div>

          <div className="nit-card">
            <h3>Sales &amp; tax</h3>
            <div className="nit-facts">
              <span>Sales rep</span><strong>{record.sales_rep_name || '—'}</strong>
              <span>Price level</span><strong>{record.price_level || '—'}</strong>
              <span>Taxable</span><strong>{yesNo(record.taxable)}</strong>
              <span>Resale cert on file</span><strong>{yesNo(record.resale_cert_on_file)}</strong>
              <span>Private label</span><strong>{yesNo(record.is_private_label)}</strong>
              <span>PL status</span><strong>{record.private_label_status || '—'}</strong>
              <span>Ship complete</span><strong>{yesNo(record.ship_complete)}</strong>
            </div>
            {record.sales_rep_name == null && record.ns_raw == null && (
              <p className="nit-caption">Rep, private-label and resale-cert fields arrive with the capture backfill (migration 20260705).</p>
            )}
          </div>
        </div>
      )}

      {tab === 'orders' && (
        <div className="nit-card">
          <h3>Recent sales orders</h3>
          {recentOrders.length === 0 && <div className="nit-empty">No synced sales orders for this customer.</div>}
          {recentOrders.length > 0 && (
            <div className="nit-table-wrap">
              <table className="nit-table">
                <thead><tr><th>SO</th><th>Date</th><th>Status</th><th className="r">Total</th><th>Memo</th></tr></thead>
                <tbody>
                  {recentOrders.map((o) => (
                    <tr key={o.id}>
                      <td><Link to={`/records/sales-orders/${encodeURIComponent(o.id)}`}><code>SO {o.order_number || o.ns_internal_id}</code></Link></td>
                      <td>{fmtDate(o.order_date)}</td>
                      <td className="nit-muted">{statusLabel(SO_STATUS, o.status)}</td>
                      <td className="r">{fmtMoney(o.total_amount)}</td>
                      <td className="nit-muted">{o.memo || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="nit-caption">Newest 20 from the synced mirror.</p>
        </div>
      )}

      {tab === 'invoices' && (
        <div className="nit-card">
          <h3>Recent invoices</h3>
          {recentInvoices.length === 0 && <div className="nit-empty">No synced invoices for this customer.</div>}
          {recentInvoices.length > 0 && (
            <div className="nit-table-wrap">
              <table className="nit-table">
                <thead><tr><th>Invoice</th><th>Date</th><th>Due</th><th>Status</th><th className="r">Total</th><th className="r">Remaining</th></tr></thead>
                <tbody>
                  {recentInvoices.map((inv) => (
                    <tr key={inv.id}>
                      <td><code>INV {inv.invoice_number}</code></td>
                      <td>{fmtDate(inv.invoice_date)}</td>
                      <td>{fmtDate(inv.due_date)}</td>
                      <td className="nit-muted">{inv.status}</td>
                      <td className="r">{fmtMoney(inv.total_amount)}</td>
                      <td className="r">{fmtMoney(inv.amount_remaining)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="nit-caption">Newest 10 from the synced mirror. Full A/R lives in Count.</p>
        </div>
      )}

      {tab === 'fields' && (
        <AllFieldsPanel
          family="customer"
          nsRaw={record.ns_raw}
          overrides={{
            salesrep: record.sales_rep_name,
            custentity18: record.private_label_status,
            custentity17: record.is_private_label,
            custentity13: record.resale_cert_on_file,
            terms: record.payment_terms,
          }}
        />
      )}

      {tab === 'related' && (
        nativeCustomerId
          ? <RelatedRecords recordType="customer" id={nativeCustomerId} />
          : (
            <div className="nit-card">
              <h3>Related records</h3>
              <div className="nit-empty">
                No native customer-master twin yet — the Related graph runs on
                <code> native_customers</code> via the identity map. The Orders and Invoices tabs
                above show this customer&apos;s synced activity in the meantime.
              </div>
            </div>
          )
      )}

      {tab === 'attachments' && <AttachmentsTab recordType="customer" recordId={id} />}
    </div>
  );
}
